import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CURRENT_PATH, DEFAULT_ROUTE } from "utils/constants";
import { usePreferences } from "contexts/preferences-context";
import { getOrderTabPathname } from "./order-tab-pathname";

const WORKSPACE_DETAILS_ROUTE = "/orders/details";

export type TabOrder = {
	id: string,
	orderId: string,
	firstPropertyAddress: string,
	lastSeen: number,
	code?: string
}

export type UseOrderTabsReturn = {
	openedOrderIdList: TabOrder[],
	selectedOrderIdTab: TabOrder | undefined,
	setSelectedOrderIdTab: Dispatch<SetStateAction<TabOrder | undefined>>,
	addOrderTab: (tabOrder: TabOrder) => void,
	removeOrderTab: (id: string) => void,
	selectOrderTab: (tabOrder: TabOrder) => void,
	updateOrderTabPropertyAddress: (id: string, address: string) => void
}

export const useOrderTabs = (): UseOrderTabsReturn => {
  const [selectedOrderIdTab, setSelectedOrderIdTab] = useState<TabOrder>();
  const history = useHistory();
  const location = useLocation();
  const { id: orderID } = useParams<{ id: string }>();
  const { recentOrders, activateOrder, removeRecentOrder, updateRecentOrderAddress } = usePreferences() as any;

  const openedOrderIdList: TabOrder[] = recentOrders.map((order) => ({
    id: order.id,
    orderId: order.orderId,
    firstPropertyAddress: order.firstPropertyAddress || '',
    lastSeen: order.lastSeen,
    code: order.code || order.orderId,
  }));

  const removeOrderTab = (id: string) => {
    removeRecentOrder(id);
    const newList = openedOrderIdList.filter((tab) => tab.id !== id);

    if (newList.length > 0) {
      setSelectedOrderIdTab(newList[0]);
      history.replace(`/orders/details/${newList[0].id}/${DEFAULT_ROUTE}`);
    } else {
      setSelectedOrderIdTab(undefined);
    }
  };

  const addOrderTab = (tabOrder: TabOrder) => {
    if (selectedOrderIdTab?.id === tabOrder.id && selectedOrderIdTab.orderId === tabOrder.orderId) return;

    activateOrder({
      id: tabOrder.id,
      orderId: tabOrder.orderId,
      firstPropertyAddress: tabOrder.firstPropertyAddress,
      code: tabOrder.code,
    });

    setSelectedOrderIdTab(tabOrder);
  };

  const updateOrderTabPropertyAddress = (id: string, address: string) => {
    updateRecentOrderAddress(id, address);
    const updated = openedOrderIdList.find((tab) => tab.id === id);
    if (updated) {
      setSelectedOrderIdTab({ ...updated, firstPropertyAddress: address });
    }
  };

  const selectOrderTab = (tabOrder: TabOrder) => {
    activateOrder({
      id: tabOrder.id,
      orderId: tabOrder.orderId,
      firstPropertyAddress: tabOrder.firstPropertyAddress,
      code: tabOrder.code,
    });

    setSelectedOrderIdTab(tabOrder);
    if (selectedOrderIdTab && !location.pathname.endsWith(WORKSPACE_DETAILS_ROUTE)) {
      history.replace(getOrderTabPathname(location.pathname, selectedOrderIdTab.id, tabOrder.id));
    } else {
      const { view } = JSON.parse(localStorage.getItem(CURRENT_PATH) || '{}');
      history.replace(`/orders/details/${tabOrder.id}/${view ?? DEFAULT_ROUTE}`);
    }
  };

  useEffect(() => {
    if (location.pathname.includes(WORKSPACE_DETAILS_ROUTE)) {
      if (location.pathname.endsWith(WORKSPACE_DETAILS_ROUTE) && openedOrderIdList.length > 0) {
        selectOrderTab(selectedOrderIdTab || openedOrderIdList[0]);
      }
    }
    // Route-entry effect: selectOrderTab mutates history, so re-running on tab/list changes would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return {
    openedOrderIdList,
    selectedOrderIdTab,
    setSelectedOrderIdTab,
    addOrderTab,
    removeOrderTab,
    selectOrderTab,
    updateOrderTabPropertyAddress
  };
};